import React, { useMemo, useState } from "react";
import { Flame, ChevronDown, RefreshCcw, ArrowUpRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { PROPERTIES } from "./data";
import { useRevealGroup } from "../../hooks/useReveal";

const ASSET_TYPES = ["All", "Residential", "Commercial", "Land & Plots", "Farmhouse"];
const LOCATIONS = ["All", "Lutyens' Delhi", "South Delhi", "Gurugram", "Noida", "Greater Noida"];
const ASSET_CLASSES = ["All", "Ultra Luxury", "Luxury", "Premium", "Commercial Office", "Farmhouse"];
const STATUSES = ["All", "Ready", "Pre-Launch", "Off-Market"];

const matchType = (p, sel) => {
  if (sel === "All") return true;
  if (sel === "Residential") return /BUNGALOW|APARTMENT|PENTHOUSE|VILLA/.test(p.type);
  if (sel === "Commercial") return /COMMERCIAL/.test(p.type);
  if (sel === "Farmhouse") return /FARMHOUSE/.test(p.type);
  if (sel === "Land & Plots") return /PLOT|LAND/.test(p.type);
  return true;
};
const matchLocation = (p, sel) => sel === "All" || p.location.toLowerCase().includes(sel.toLowerCase());
const matchClass = (p, sel) => {
  if (sel === "All") return true;
  return p.type.toUpperCase().includes(sel.toUpperCase());
};
const matchStatus = (p, sel) => {
  if (sel === "All") return true;
  return p.badge.replace("-", " ").toUpperCase().includes(sel.toUpperCase());
};

const FilterDropdown = ({ label, value, options, onChange, testId }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        className="flex items-center gap-2 px-5 py-3 font-ui text-[10px] uppercase track-label bg-num-cream text-num-forest border border-num-gold/50 hover:border-num-gold transition-colors"
        data-testid={testId}
      >
        <span className="text-num-stone">{label}:</span>
        <span className="font-medium">{value}</span>
        <ChevronDown size={12} className="text-num-gold" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="start"
      className="bg-num-cream border border-num-gold/40 rounded-none min-w-[200px]"
    >
      <DropdownMenuLabel className="font-ui text-[9px] uppercase track-eyebrow text-num-stone">
        {label}
      </DropdownMenuLabel>
      <DropdownMenuSeparator className="bg-num-gold/30" />
      {options.map((opt) => (
        <DropdownMenuItem
          key={opt}
          onSelect={() => onChange(opt)}
          className="font-ui text-[11px] text-num-forest cursor-pointer focus:bg-num-linen focus:text-num-forest data-[highlighted]:bg-num-linen"
          data-testid={`${testId}-option-${opt.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {opt}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const PropertyCard = ({ p, hero }) => (
  <div
    className={`reveal bg-num-cream group transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(28,6,6,0.45)] ${
      hero ? "lg:col-span-2 lg:row-span-1" : ""
    }`}
    data-testid={`property-card-${p.code}`}
  >
    <div className={`relative img-zoom ${hero ? "aspect-[2/1]" : "aspect-[4/3]"} bg-num-ivory`}>
      <img
        src={p.image}
        alt={p.location}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center"
        style={{ background: "rgba(28,6,6,0.42)" }}
      >
        <span className="font-ui text-[11px] uppercase track-label text-num-cream flex items-center gap-2">
          View Property <ArrowUpRight size={14} className="text-num-gold" />
        </span>
      </div>
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span
          className={`font-ui text-[9px] uppercase track-label px-3 py-1.5 ${
            p.badgeTone === "hot"
              ? "bg-num-gold text-num-deep"
              : p.badgeTone === "ready"
              ? "bg-num-cream text-num-forest border border-num-forest/30"
              : "bg-num-deep/80 text-num-gold border border-num-gold/40"
          }`}
        >
          {p.badge}
        </span>
      </div>
    </div>

    <div className="p-7">
      <div className="flex items-start justify-between gap-4">
        <span className="font-ui text-[9px] track-eyebrow text-num-stone uppercase">
          {p.code}
        </span>
        <span className="font-ui text-[9px] track-label text-num-gold border border-num-gold/50 px-2 py-1 uppercase">
          {p.type}
        </span>
      </div>

      <div className="mt-4 font-ui text-[10px] track-label text-num-forest uppercase">
        {p.location}
      </div>
      <div className="mt-2 font-display text-num-espresso" style={{ fontSize: "20px" }}>
        {p.config}
      </div>

      <div className="rule-gold my-6" />

      <dl className="space-y-3">
        <div className="flex items-baseline justify-between font-ui">
          <dt className="text-[10px] track-label uppercase text-num-stone">Market Rate</dt>
          <dd className="num-editorial text-num-forest" style={{ fontSize: 22 }}>{p.rate}</dd>
        </div>
        <div className="flex items-baseline justify-between font-ui">
          <dt className="text-[10px] track-label uppercase text-num-stone">Capital Value</dt>
          <dd className="num-editorial text-num-forest" style={{ fontSize: 26 }}>{p.value}</dd>
        </div>
        <div>
          <div className="flex items-baseline justify-between font-ui">
            <dt className="text-[10px] track-label uppercase text-num-stone">5-Yr Growth</dt>
            <dd className="num-editorial text-num-forest" style={{ fontSize: 16 }}>{p.growth}</dd>
          </div>
          <div className="mt-1.5 h-1 w-full bg-num-linen overflow-hidden">
            <span
              className="block h-full bg-num-forest"
              style={{ width: parseGrowthWidth(p.growth) }}
            />
          </div>
        </div>
        {p.yield && (
          <div className="flex items-baseline justify-between font-ui">
            <dt className="text-[10px] track-label uppercase text-num-stone">Rental Yield</dt>
            <dd className="num-editorial text-num-forest" style={{ fontSize: 16 }}>{p.yield}</dd>
          </div>
        )}
      </dl>

      <div className="rule-gold my-6" />

      <div className="flex justify-end">
        <a
          href="#contact"
          className="inline-flex items-center gap-2 font-ui text-[10px] uppercase track-label text-num-forest border-b border-num-gold pb-1 hover:text-num-gold transition-colors"
          data-testid={`property-cta-${p.code}`}
        >
          Request Details <ArrowUpRight size={12} />
        </a>
      </div>
    </div>
  </div>
);

function parseGrowthWidth(g) {
  // Extracts the upper percentage and caps at 60% of bar width
  const m = g.match(/(\d+)\s*%/g);
  if (!m) return "30%";
  const last = parseInt(m[m.length - 1], 10);
  const w = Math.min(Math.max(last, 10), 60);
  return `${w}%`;
}

const Dealroom = () => {
  const ref = useRevealGroup(".reveal");
  const [hot, setHot] = useState(false);
  const [type, setType] = useState("All");
  const [loc, setLoc] = useState("All");
  const [cls, setCls] = useState("All");
  const [status, setStatus] = useState("All");

  const filtered = useMemo(() => {
    return PROPERTIES.filter((p) => {
      if (hot && p.badgeTone !== "hot" && p.badge !== "HOT DEAL") return false;
      return (
        matchType(p, type) &&
        matchLocation(p, loc) &&
        matchClass(p, cls) &&
        matchStatus(p, status)
      );
    });
  }, [hot, type, loc, cls, status]);

  const reset = () => {
    setHot(false);
    setType("All");
    setLoc("All");
    setCls("All");
    setStatus("All");
  };

  return (
    <section ref={ref} className="bg-grain py-24 lg:py-32" data-testid="dealroom-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-[800px] reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-num-gold" />
            <span className="font-ui text-[9px] uppercase text-num-gold track-eyebrow">
              The Numbardar Dealroom
            </span>
          </div>
          <h2
            className="font-display font-medium text-num-forest leading-[1.05]"
            style={{ fontSize: "clamp(38px, 5vw, 60px)" }}
          >
            Inventory of{" "}
            <span className="font-italic italic font-normal text-num-gold">Distinction</span>
          </h2>
          <p className="font-body italic text-[15px] mt-5" style={{ color: "#8C7E6A" }}>
            Curated assets. Pre-vetted locations. Transparent financials. Every property earns its
            place.
          </p>
        </div>

        {/* Filter bar */}
        <div className="reveal delay-1 mt-12 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setHot((v) => !v)}
            className={`inline-flex items-center gap-2 px-5 py-3 font-ui text-[10px] uppercase track-label transition-colors ${
              hot
                ? "bg-num-forest text-num-cream border border-num-forest"
                : "bg-num-gold text-num-deep border border-num-gold"
            }`}
            data-testid="filter-hot-deals"
          >
            <Flame size={12} />
            Hot Deals
          </button>
          <FilterDropdown label="Type" value={type} options={ASSET_TYPES} onChange={setType} testId="filter-type" />
          <FilterDropdown label="Location" value={loc} options={LOCATIONS} onChange={setLoc} testId="filter-location" />
          <FilterDropdown label="Class" value={cls} options={ASSET_CLASSES} onChange={setCls} testId="filter-class" />
          <FilterDropdown label="Status" value={status} options={STATUSES} onChange={setStatus} testId="filter-status" />
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 px-4 py-3 font-ui text-[10px] uppercase track-label text-num-forest hover:text-num-gold transition-colors"
            data-testid="filter-reset"
          >
            <RefreshCcw size={12} /> Reset
          </button>
          <span className="ml-auto font-ui text-[10px] track-label uppercase text-num-stone">
            {filtered.length} {filtered.length === 1 ? "property" : "properties"} shown
          </span>
        </div>

        {/* Grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((p) => (
            <PropertyCard key={p.code} p={p} hero={p.feature} />
          ))}
        </div>

        {/* Footer note */}
        <p className="reveal mt-16 max-w-[680px] font-body italic text-[13px] leading-[1.85]" style={{ color: "#8C7E6A" }}>
          Property names, developer details and full financials are revealed on enquiry, after an
          advisor qualification call. This is by design — discretion is the foundation of how we
          serve.
        </p>
      </div>
    </section>
  );
};

export default Dealroom;
